import { useState, useEffect } from 'react'
import {
	Box,
	Typography,
	Button,
	IconButton,
	useMediaQuery,
	useTheme
} from '@mui/material'
import {
	Close as CloseIcon
} from '@mui/icons-material'
import PetForm from './PetForm'
import PetAvatar from './PetAvatar'

// Nami lavender theme tokens (from the design handoff)
const CMPET = {
	surface: '#F5F3FB',
	border: '#E7E2F3',
	field: '#FFFFFF',
	text: '#2E2A40',
	muted: '#8B85A4',
	primary: '#7C6BD9',
	primaryDeep: '#5F4EC2',
	primarySoft: '#EDE9FB',
	danger: '#BE4B45',
	dangerSoft: '#FBECEB'
}
const FONTPET = "'Outfit', sans-serif"

function AddEditPetPage({ pet, onSave, onClose, onDelete, isMobile, merchantId, clientId, onAvatarUploaded, padding = '16px', width = '316px', flexShrink = 0, embedded = false }) {
	const theme = useTheme()
	const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'))
	const mobile = isMobile !== undefined ? isMobile : isSmallScreen
	// `embedded` renders the same compact mobile layout, but in-flow (filling its
	// parent — e.g. the desktop add-appointment side panel) instead of as a
	// full-screen fixed overlay.
	const useMobileLayout = mobile || embedded

	const [formData, setFormData] = useState({
		name: '',
		species: '',
		breed: '',
		birthday: '',
		sex: '',
		weight: '',
		medical_info: '',
		grooming_info: '',
		personality: ''
	})

	// Avatar state for new pets (pending upload)
	const [pendingAvatarBlob, setPendingAvatarBlob] = useState(null)
	const [pendingAvatarUrl, setPendingAvatarUrl] = useState(null)

	useEffect(() => {
		if (pet) {
			setFormData({
				name: pet.name || '',
				species: pet.species || '',
				breed: pet.breed || '',
				birthday: pet.birthday || '',
				sex: pet.sex || '',
				weight: pet.weight ? pet.weight.toString() : '',
				medical_info: pet.medical_info || '',
				grooming_info: pet.grooming_info || '',
				personality: pet.personality || ''
			})
		}
		// Reset pending avatar when pet changes
		setPendingAvatarBlob(null)
		setPendingAvatarUrl(null)
	}, [pet])

	// Cleanup preview URL when component unmounts
	useEffect(() => {
		return () => {
			if (pendingAvatarUrl) {
				URL.revokeObjectURL(pendingAvatarUrl)
			}
		}
	}, [pendingAvatarUrl])

	const handleAvatarChange = (blob, previewUrl) => {
		// Cleanup old preview URL
		if (pendingAvatarUrl) {
			URL.revokeObjectURL(pendingAvatarUrl)
		}
		setPendingAvatarBlob(blob)
		setPendingAvatarUrl(previewUrl)
	}

	const handleSave = () => {
		// Validate that name is not empty
		if (!formData.name.trim()) {
			return
		}

		// Convert empty strings to null for optional fields
		const petData = {
			name: formData.name.trim(),
			species: formData.species.trim() || null,
			breed: formData.breed.trim() || null,
			birthday: formData.birthday || null,
			sex: formData.sex || null,
			weight: formData.weight ? parseFloat(formData.weight) : null,
			medical_info: formData.medical_info.trim() || null,
			grooming_info: formData.grooming_info.trim() || null,
			personality: formData.personality.trim() || null
		}

		// Include pending avatar blob for new pets
		if (pendingAvatarBlob) {
			petData._pendingAvatarBlob = pendingAvatarBlob
		}

		onSave(petData)
	}

	// Check if form is valid (only name is required)
	const isFormValid = formData.name.trim().length > 0

	const handleDeleteClick = () => {
		if (window.confirm(`Are you sure you want to delete ${pet?.name || 'this pet'}?`)) {
			onDelete(pet)
		}
	}

	// Mobile View — Nami "Edit pet · profile form". Full-screen fixed overlay on
	// real mobile; in-flow (fills its parent panel) when `embedded` on desktop.
	if (useMobileLayout) {
		return (
			<Box sx={{
				...(embedded && !mobile
					? { flex: 1, minHeight: 0, height: '100%' }
					: { position: 'fixed', inset: 0, zIndex: 1300 }),
				display: 'flex',
				flexDirection: 'column',
				bgcolor: CMPET.field
			}}>
				{/* Header */}
				<Box sx={{
					flex: 'none',
					display: 'flex',
					alignItems: 'center',
					gap: '11px',
					padding: '10px 16px',
					bgcolor: CMPET.field,
					borderBottom: `1px solid ${CMPET.border}`
				}}>
					<IconButton
						onClick={onClose}
						aria-label="Back"
						sx={{ width: 40, height: 40, ml: '-8px', borderRadius: '11px', color: CMPET.text, '&:hover': { bgcolor: CMPET.surface } }}
					>
						<svg width="17" height="17" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg"><path d="M10 3.5L5.5 8l4.5 4.5" /></svg>
					</IconButton>
					<Typography sx={{
						flex: 1,
						minWidth: 0,
						fontFamily: FONTPET,
						fontSize: '19px',
						fontWeight: 800,
						letterSpacing: '-0.015em',
						color: CMPET.text,
						whiteSpace: 'nowrap',
						overflow: 'hidden',
						textOverflow: 'ellipsis'
					}}>
						{pet ? 'Edit pet' : 'Add pet'}
					</Typography>
					<Button
						onClick={handleSave}
						disabled={!isFormValid}
						startIcon={
							<svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg"><path d="M3 8.5l3.2 3L13 4.5" /></svg>
						}
						sx={{
							height: '38px',
							px: '15px',
							border: 'none',
							borderRadius: '11px',
							bgcolor: CMPET.primary,
							color: '#FFFFFF',
							textTransform: 'none',
							fontFamily: FONTPET,
							fontWeight: 700,
							fontSize: '13.5px',
							boxShadow: `0 2px 8px ${CMPET.primarySoft}`,
							'&:hover': { bgcolor: CMPET.primaryDeep },
							'&:disabled': { bgcolor: '#CBBEF0', color: '#FFFFFF', boxShadow: 'none' },
							'& .MuiButton-startIcon': { marginRight: '6px' }
						}}
					>
						Save
					</Button>
				</Box>

				{/* Body — content is centered with a max width so this mobile layout
				    also reads well when reused on a wider desktop panel/screen. */}
				<Box sx={{
					flex: 1,
					minHeight: 0,
					overflowY: 'auto',
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					gap: '14px',
					padding: '16px 16px 24px'
				}}>
					{/* Photo */}
					<Box sx={{ display: 'flex', justifyContent: 'center', pt: '4px' }}>
						<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '9px' }}>
							<PetAvatar
								petId={pet?.id}
								merchantId={merchantId}
								clientId={clientId}
								size={100}
								editable={true}
								onAvatarChange={handleAvatarChange}
								onUploadComplete={onAvatarUploaded}
								pendingAvatarUrl={pendingAvatarUrl}
							/>
							<Typography sx={{ fontFamily: FONTPET, fontSize: '13px', fontWeight: 600, color: CMPET.primaryDeep }}>
								{pet ? 'Replace photo' : 'Add photo'}
							</Typography>
						</Box>
					</Box>

					{/* Identity */}
					<Box sx={{ width: '100%', maxWidth: '560px', bgcolor: CMPET.field, border: `1px solid ${CMPET.border}`, borderRadius: '16px', padding: '15px 15px 16px', boxShadow: '0 1px 3px rgba(46, 42, 64, 0.04)' }}>
						<PetForm
							formData={formData}
							setFormData={setFormData}
							layout="grid"
							section="identity"
							stack
						/>
					</Box>

					{/* Care notes */}
					<Box sx={{ width: '100%', maxWidth: '560px', bgcolor: CMPET.field, border: `1px solid ${CMPET.border}`, borderRadius: '16px', padding: '15px 15px 16px', boxShadow: '0 1px 3px rgba(46, 42, 64, 0.04)' }}>
						<PetForm
							formData={formData}
							setFormData={setFormData}
							layout="grid"
							section="notes"
							stack
						/>
					</Box>

					{/* Delete Pet Button (only for editing) */}
					{pet && onDelete && (
						<Button
							onClick={handleDeleteClick}
							sx={{
								alignSelf: 'center',
								height: '40px',
								px: '13px',
								border: 'none',
								bgcolor: 'transparent',
								borderRadius: '10px',
								textTransform: 'none',
								fontFamily: FONTPET,
								fontWeight: 650,
								fontSize: '13.5px',
								color: CMPET.danger,
								'&:hover': { bgcolor: CMPET.dangerSoft },
								'& .MuiButton-startIcon': { marginRight: '7px' }
							}}
							startIcon={
								<svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg"><path d="M2.8 4.4h10.4M6 4.4V3.1a1 1 0 011-1h2a1 1 0 011 1v1.3M4 4.4l.6 8.2a1 1 0 001 .9h4.8a1 1 0 001-.9l.6-8.2" /></svg>
							}
						>
							Delete pet
						</Button>
					)}
				</Box>
			</Box>
		)
	}

	// Desktop View — Nami "Edit pet · profile form" (full-width separate screen)
	return (
		<Box sx={{ display: 'flex', flexDirection: 'column' }}>
			<Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', minHeight: '40px', mb: '18px' }}>
				<IconButton
					onClick={onClose}
					aria-label="Back"
					sx={{ width: 34, height: 34, border: `1px solid ${CMPET.border}`, borderRadius: '9px', bgcolor: CMPET.field, color: CMPET.muted, transform: 'rotate(90deg)', '&:hover': { color: CMPET.primaryDeep, borderColor: CMPET.primary } }}
				>
					<svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg"><path d="M4.5 6.5L8 10l3.5-3.5" /></svg>
				</IconButton>
				<Typography sx={{ fontFamily: FONTPET, fontSize: '23px', fontWeight: 800, letterSpacing: '-0.02em', color: CMPET.text }}>
					{pet ? 'Edit pet' : 'Add pet'}
				</Typography>
			</Box>

			<Box sx={{ display: 'flex', flexDirection: 'column', bgcolor: CMPET.field, border: `1px solid ${CMPET.border}`, borderRadius: '14px', boxShadow: '0 1px 3px rgba(46, 42, 64, 0.04)', overflow: 'hidden' }}>
				<Box sx={{ padding: '26px 28px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
					{/* Top: photo + identity */}
					<Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '300px 1fr' }, gap: '30px', alignItems: 'start' }}>
						<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start', gap: '12px', textAlign: 'center', pt: '4px' }}>
							<PetAvatar
								petId={pet?.id}
								merchantId={merchantId}
								clientId={clientId}
								size={136}
								editable={true}
								onAvatarChange={handleAvatarChange}
								onUploadComplete={onAvatarUploaded}
								pendingAvatarUrl={pendingAvatarUrl}
							/>
							<Typography sx={{ fontFamily: FONTPET, fontSize: '13px', fontWeight: 600, color: CMPET.primaryDeep }}>
								{pet ? 'Replace photo' : 'Add photo'}
							</Typography>
						</Box>

						<Box sx={{ minWidth: 0 }}>
							<PetForm
								formData={formData}
								setFormData={setFormData}
								compact={false}
								showLabels={true}
								layout="grid"
								section="identity"
							/>
						</Box>
					</Box>

					{/* Separator above Care notes (full-bleed across the card) */}
					<Box sx={{ height: '1px', bgcolor: CMPET.border, mx: '-28px' }} />

					{/* Care notes: full width below the photo */}
					<PetForm
						formData={formData}
						setFormData={setFormData}
						compact={false}
						showLabels={true}
						layout="grid"
						section="notes"
					/>
				</Box>

				<Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '15px 24px', borderTop: `1px solid ${CMPET.border}` }}>
					{pet && onDelete ? (
						<Button
							onClick={handleDeleteClick}
							sx={{ height: '40px', px: '13px', border: 'none', bgcolor: 'transparent', borderRadius: '10px', textTransform: 'none', fontFamily: FONTPET, fontWeight: 650, fontSize: '13.5px', color: CMPET.danger, '&:hover': { bgcolor: CMPET.dangerSoft }, '& .MuiButton-startIcon': { marginRight: '7px' } }}
							startIcon={
								<svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg"><path d="M2.8 4.4h10.4M6 4.4V3.1a1 1 0 011-1h2a1 1 0 011 1v1.3M4 4.4l.6 8.2a1 1 0 001 .9h4.8a1 1 0 001-.9l.6-8.2" /></svg>
							}
						>
							Delete pet
						</Button>
					) : <Box />}
					<Box sx={{ display: 'flex', gap: '10px', marginLeft: 'auto' }}>
						<Button
							onClick={onClose}
							sx={{ height: '40px', px: '16px', border: `1.5px solid ${CMPET.border}`, borderRadius: '10px', bgcolor: CMPET.field, textTransform: 'none', fontFamily: FONTPET, fontWeight: 650, fontSize: '13.5px', color: CMPET.text, '&:hover': { borderColor: CMPET.primary, color: CMPET.primaryDeep, bgcolor: CMPET.field } }}
						>
							Cancel
						</Button>
						<Button
							onClick={handleSave}
							disabled={!isFormValid}
							sx={{ height: '40px', px: '16px', border: 'none', borderRadius: '10px', bgcolor: CMPET.primary, color: '#FFFFFF', textTransform: 'none', fontFamily: FONTPET, fontWeight: 700, fontSize: '13.5px', boxShadow: `0 2px 8px ${CMPET.primarySoft}`, '&:hover': { bgcolor: CMPET.primaryDeep }, '&:disabled': { bgcolor: '#CBBEF0', color: '#FFFFFF', boxShadow: 'none' } }}
						>
							{pet ? 'Save changes' : 'Add pet'}
						</Button>
					</Box>
				</Box>
			</Box>
		</Box>
	)
}

export default AddEditPetPage
